import { useState } from "react";

import { Button, Modal } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";
import { observer } from "mobx-react-lite";

import {
  isLastCreateApplicationStep,
  scrollCreateApplicationPageToTop,
  scrollToFirstInvalidCreateApplicationField,
  usePageStore,
} from "../create-application-page.lib";
import { useSaveApplicationDraft } from "../hooks/use-save-create-application-draft";
import { useApplicationDraftId } from "../hooks/use-create-application-draft-id";

type CreateApplicationActionsProps = {
  className?: string;
};

export const CreateApplicationActions = observer(({ className }: CreateApplicationActionsProps) => {
  const pageStore = usePageStore();
  const { saveDraft, submitApplication, isSaving } = useSaveApplicationDraft();
  const { draftId } = useApplicationDraftId();
  const [isSubmitConfirmOpen, setIsSubmitConfirmOpen] = useState(false);

  const activeStep = pageStore?.activeStep ?? "type";
  const isTypeStep = activeStep === "type";
  const isObjectDataStep = activeStep === "object-data";
  const isDocumentsStep = activeStep === "documents";
  const isApplicantStep = activeStep === "applicant";
  const isLastStep = isLastCreateApplicationStep(activeStep);
  const isEditingExistingDraft = Boolean(draftId);
  const canSaveDraft = Boolean(pageStore?.selectedCategory) && (!isEditingExistingDraft || Boolean(pageStore?.isDraftDirty));
  const canGoNextFromTypeStep = Boolean(pageStore?.selectedCategory);
  const isRevisionResubmit = pageStore?.editingMode === "revision";

  const handleBack = () => {
    pageStore?.goToPreviousStep();
    scrollCreateApplicationPageToTop();
  };

  const handleNext = async () => {
    if (!pageStore) {
      return;
    }

    if (isTypeStep && !pageStore.selectedCategory) {
      return;
    }

    if (isApplicantStep) {
      const isValid = await pageStore.validateApplicantStep();

      if (!isValid) {
        return;
      }
    }

    if (isObjectDataStep) {
      const isValid = await pageStore.validateObjectDataStep();

      if (!isValid) {
        return;
      }
    }

    if (isDocumentsStep) {
      const isValid = pageStore.validateDocumentsStep();

      if (!isValid) {
        scrollToFirstInvalidCreateApplicationField();
        return;
      }
    }

    if (isLastStep) {
      setIsSubmitConfirmOpen(true);
      return;
    }

    pageStore.goToNextStep();
    scrollCreateApplicationPageToTop();
  };

  const handleSubmitConfirmOpenChange = (open: boolean) => {
    if (!isSaving) {
      setIsSubmitConfirmOpen(open);
    }
  };

  const handleCancelSubmit = () => {
    setIsSubmitConfirmOpen(false);
  };

  const handleConfirmSubmit = () => {
    setIsSubmitConfirmOpen(false);
    submitApplication();
  };

  return (
    <>
      <div className={cn("grid gap-4 lg:grid-cols-3", className)}>
        <div className="flex justify-start">
          {!isTypeStep && (
            <Button type="button" theme="secondary" size="lg" disabled={isSaving} onClick={handleBack}>
              Назад
            </Button>
          )}
        </div>

        <div className="flex justify-center">
          <Button type="button" theme="secondary" size="lg" disabled={!canSaveDraft || isSaving} onClick={saveDraft}>
            {isEditingExistingDraft ? "Сохранить изменения" : "Сохранить черновик"}
          </Button>
        </div>

        <div className="flex justify-end">
          <Button type="button" theme="primary" size="lg" disabled={isSaving || (isTypeStep && !canGoNextFromTypeStep)} onClick={handleNext}>
            {isLastStep ? (isRevisionResubmit ? "Повторно подать заявку" : "Отправить") : "Далее"}
          </Button>
        </div>
      </div>

      <Modal.Root isOpen={isSubmitConfirmOpen} setIsOpen={handleSubmitConfirmOpenChange}>
        <Modal.Content className="w-[700px]">
          <Modal.Title>Отправка заявки в РЖД</Modal.Title>
          <div className="flex flex-col gap-4 px-8 pb-2 pt-6">
            <h3 className="heading-h4 text-type-common">Отправить заявку в РЖД?</h3>
            <p className="paragraph-base text-type-minor">Редактировать данные после отправки будет невозможно.</p>
          </div>
          <div className="flex shrink-0 gap-4 px-8 pb-6 pt-8">
            <Button type="button" theme="primary" size="lg" disabled={isSaving} onClick={handleConfirmSubmit}>
              Отправить
            </Button>
            <Button type="button" theme="secondary" size="lg" disabled={isSaving} onClick={handleCancelSubmit}>
              Отмена
            </Button>
          </div>
        </Modal.Content>
      </Modal.Root>
    </>
  );
});
